/**
 * KAN-834: чистые функции приложения менеджера: строки карточек, сортировка
 * лидов по активности, звонки с записью, строка внимания из карточки клиента.
 */
import type { AttentionAction, AttentionItem } from '@/services/api/dashboard';
import type { Client, ClientSenderAccount } from '@/types/client';
import type { ManagerBrief } from '@/types/manager';
import { TAKEN_TO_WORK_TEXT } from '@/lib/workActions';
import { WORK_CONTINUES_TEXT } from '@/lib/workContinues';

/** Сколько лидов грузит вкладка "Мои лиды" за раз. */
export const MOBILE_LEADS_PAGE_SIZE = 100;

export function clientTitle(client: Pick<Client, 'name' | 'phone'>): string {
  return client.name || client.phone || 'Клиент';
}

/** "Организация, город" (что известно), пусто, если ничего. */
export function clientPlace(client: Pick<Client, 'client_company' | 'company'>): string {
  const org = client.client_company?.name || client.company || '';
  const city = client.client_company?.city || '';
  return [org, city].filter(Boolean).join(', ');
}

function time(value: string | null | undefined): number {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** Лиды по последней активности: свежие сверху, без сообщений в конце. */
export function sortLeadsByActivity(clients: readonly Client[]): Client[] {
  return [...clients].sort(
    (a, b) => time(b.last_message_at ?? b.updated_at) - time(a.last_message_at ?? a.updated_at)
  );
}

/**
 * Строка блока "Требуют внимания" из карточки клиента: итог в карточке идёт
 * через те же хуки, что и строки дашборда (useAttentionDone, useAttentionSuccess),
 * им нужны id, имя и менеджер клиента.
 */
export function attentionItemFromClient(client: Client): AttentionItem {
  return {
    client_id: client.id,
    name: client.name,
    company: client.client_company?.name || client.company || '',
    reasons: [],
    reason_text: '',
    since: client.updated_at,
    last_message: null,
    sender_account: null,
    estimate_id: null,
    manager: client.manager ?? null,
    phone: client.phone || null,
  };
}

/**
 * KAN-852: чей клиент для подписи в приложении: "ничей", "ваш" или
 * "у <ФИО менеджера>". selfId: запись справочника текущего менеджера.
 */
export function clientOwnerLabel(
  manager: Pick<ManagerBrief, 'id' | 'full_name'> | null | undefined,
  selfId: string | null
): string {
  if (!manager) return 'ничей';
  if (selfId !== null && manager.id === selfId) return 'ваш';
  return `у ${manager.full_name}`;
}

/** KAN-856: подпись Telegram клиента, когда username нет. */
export const TELEGRAM_BY_PHONE_TEXT = 'Telegram по номеру';
export const TELEGRAM_BY_ID_TEXT = 'Telegram (id)';

/**
 * KAN-852, KAN-856: ссылка на Telegram клиента и подпись к ней:
 *   - username: https://t.me/<username>, подпись "@username";
 *   - иначе телефон: https://t.me/+<цифры> (откроет профиль, если клиент
 *     разрешил поиск по номеру), подпись "Telegram по номеру";
 *   - иначе telegram_id: tg://user?id=<id>, подпись "Telegram (id)";
 * null: нечем открыть.
 */
export function clientTelegramLink(
  client: Pick<Client, 'telegram_username' | 'phone'> & { telegram_id?: number | string | null }
): { href: string; text: string } | null {
  const username = (client.telegram_username || '').replace(/^@/, '').trim();
  if (username) return { href: `https://t.me/${username}`, text: `@${username}` };
  const digits = (client.phone || '').replace(/\D/g, '');
  if (digits) return { href: `https://t.me/+${digits}`, text: TELEGRAM_BY_PHONE_TEXT };
  const id = String(client.telegram_id ?? '').trim();
  if (/^\d+$/.test(id)) return { href: `tg://user?id=${id}`, text: TELEGRAM_BY_ID_TEXT };
  return null;
}

/** KAN-852: наш Telegram-аккаунт переписки одной строкой: "Анна, RU-2 (@seller)". */
export function senderAccountText(
  account:
    | (Pick<ClientSenderAccount, 'account_id' | 'label' | 'phone' | 'username' | 'persona_name'> & {
        profile_name?: string;
      })
    | null
    | undefined
): string {
  if (!account) return '';
  const name = account.persona_name || account.profile_name;
  const label = account.label || account.phone || account.account_id;
  const head = [name, label].filter(Boolean).join(', ');
  return account.username ? `${head} (@${account.username})` : head;
}

/** Тост после сохранения итога. KAN-852: done = взять в работу. */
export const OUTCOME_SAVED_TEXT: Record<AttentionAction, string> = {
  done: TAKEN_TO_WORK_TEXT,
  success: 'Отмечено: успех',
  refused: 'Отмечено: отказ',
  work_continues: WORK_CONTINUES_TEXT,
};
